import { yupResolver } from '@hookform/resolvers/yup'
import React, { ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import {
  CreditApplication,
  ServiceProviderInput,
  ServiceProviderSchema,
} from '../../data/schemas/PrequalificationSchema'
import { useAppSelector } from '../../data/store'
import { creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { Enum } from '../../data/types/constants'
import { dynamicObject } from '../InitialFinancingInformationContainer/InitialFinancingInformationContainer'
import { getValuesFromObject, validateObjectValue } from '../utils/utils'
import { MerchantDto } from '../../data/types/Merchant'
import allApis from '../../data/api'
import Alert from '../components/Alert'
import { removeDiacritics } from '../utils/Formatter'

type Props = {
  stepProgression: ReactElement
  creditApplication: CreditApplication
  onFormSubmitted: (data: dynamicObject) => void
  getFooter: (handleBack: () => void, disableNext: boolean) => ReactElement
  handleBack: (data: dynamicObject) => void
}

function ServiceProviderForm({
  getFooter: footer,
  handleBack,
  creditApplication,
  stepProgression,
  onFormSubmitted,
}: Props) {
  const { t } = useTranslation()
  const MERCHANTS_DROP_DOWN_LIMIT = 5
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    watch,
    getValues,
    reset,
    trigger,
  } = useForm<ServiceProviderInput>({
    mode: 'onBlur',
    resolver: yupResolver(ServiceProviderSchema),
  })

  const currentMerchantId = useAppSelector(creditApplicationSelectors.getCurrentMerchantId)
  const apiClient = useAppSelector(creditApplicationSelectors.getApiClient)
  const financingProgramId = 'personal'
  const [query, setQuery] = useState('')
  const [merchants, setMerchants] = useState<MerchantDto[]>([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const selectedMerchant = watch('merchantName')
  const selectedMerchantId = watch('merchantId')
  const ifinanceMerchantId = `alis*${process.env.REACT_APP_IFINANCE_MERCHANT_ID}`
  const [disableNext, setDisableNext] = useState(true)
  const excludedWords = [
    '.',
    ',',
    ':',
    'le',
    'la',
    'les',
    'de',
    'du',
    'des',
    'au',
    'aux',
    'dans',
    'par',
    'pour',
    'sur',
    'the',
    'one',
    'and',
    'on',
    'of',
    'a',
    'dr.',
    'dr',
  ]

  React.useEffect(() => {
    const defaultValues = getValuesFromObject(Object.keys(ServiceProviderSchema.getDefault()), creditApplication)
    reset(defaultValues)
    validateObjectValue(getValues(), trigger)
  }, [creditApplication])

  const getRelevantWordsFromQuery = (queryString: string) => {
    const words = queryString.split(' ')
    return words.filter((word) => !excludedWords.includes(word))
  }

  const updateQuery = (e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)
  const deboucedUpdateQuery = debounce(updateQuery, 1000)
  const onMerchantNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchants([])
    setLoadingMerchants(true && e.target.value.trim().length > 3)
    setValue('merchantName', e.target.value)
    setValue('merchantId', ifinanceMerchantId)
    if (e.target.value.trim().length > 3) deboucedUpdateQuery(e)
  }

  const transformLoanPurposeBeforePostMerchant = (currentLoanPurposeId: string) => {
    if (
      currentLoanPurposeId === Enum.ELoanPurpose.HomeImprovement ||
      currentLoanPurposeId === Enum.ELoanPurpose.Other
    ) {
      return Enum.ELoanPurpose.GoodsAndServices
    }
    return currentLoanPurposeId
  }

  React.useEffect(() => {
    setDisableNext(merchants.length !== 0 && selectedMerchant !== '')
  }, [selectedMerchant, merchants])

  React.useEffect(() => {
    let isCancelled = false
    if (query && query.trim()) {
      const dto = {
        financingProgramId,
        nameContains: query,
        serviceCategory: transformLoanPurposeBeforePostMerchant(creditApplication.loanPurposeId),
      }
      allApis.config
        .getMerchants(apiClient, dto)
        .then((result) => {
          if (!isCancelled) {
            setMerchants(() => result)
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setLoadingMerchants(false)
          }
        })
    }
    return () => {
      isCancelled = true
    }
  }, [query])

  const bottomRef = React.useRef<HTMLDivElement | null>(null)

  const goBack = () => {
    handleBack(getValues())
  }

  const isIfinanceMerchant = currentMerchantId === Number(process.env.REACT_APP_IFINANCE_MERCHANT_ID)

  const requiresSpecialistName =
    creditApplication.loanPurposeId === Enum.ELoanPurpose.Medical ||
    creditApplication.loanPurposeId === Enum.ELoanPurpose.Aesthetics ||
    creditApplication.loanPurposeId === Enum.ELoanPurpose.Veterinary ||
    creditApplication.loanPurposeId === Enum.ELoanPurpose.Dentistry

  const handleClick = (id: string) => {
    const merchant = merchants.find((m) => m.id === id)
    if (merchant) {
      setMerchants([])
      setValue('merchantName', merchant.name)
      setValue('merchantId', merchant.id)
    }
  }

  const handleClickContinueWithoutSelection = () => {
    setMerchants([])
    setValue('merchantId', ifinanceMerchantId)
  }

  const styleDivBackgroundWhite = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.currentTarget.style.backgroundColor = 'white'
    e.currentTarget.style.border = '1px solid #BEBEBE'
  }

  const styleDivBackgroundF4F4F4 = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.currentTarget.style.backgroundColor = '#F4F4F4'
    e.currentTarget.style.border = '1px solid #08abf0'
  }

  return (
    <main className="form-section step-3-active">
      {stepProgression}
      <section className="step-content">
        <h1 className="h2" style={{ textAlign: 'center' }}>
          {t('serviceProvider.provider')}
        </h1>

        {isIfinanceMerchant && (
          <div className="paragraph">
            <p>{t('serviceProvider.providerSelection')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onFormSubmitted)}>
          {errors.merchantName && <Alert type="error" message={t<string>('common.merchantNameSelectError')} />}
          <div className={`control-group ${requiresSpecialistName && 'mid'} upper ${errors.merchantName && 'error'}`}>
            <label htmlFor="name">
              {creditApplication.loanPurposeId === Enum.ELoanPurpose.Medical
                ? t<string>('serviceProvider.merchantName')
                : t<string>('serviceProvider.providerName')}
            </label>
            <div className="input-with-icon">
              {selectedMerchantId && selectedMerchantId !== ifinanceMerchantId && <i className="fa fa-check icon" />}
              {loadingMerchants && <i className="fa fa-spinner fa-spin icon" style={{ top: '35%' }} />}
              <input
                type="text"
                id="merchantName"
                maxLength={100}
                onChange={onMerchantNameChanged}
                value={selectedMerchant}
                autoComplete="off"
              />
            </div>
          </div>

          {requiresSpecialistName && (
            <div
              className={`control-group ${requiresSpecialistName && 'mid'} bottom ${errors.specialistName && 'error'}`}
            >
              <label htmlFor="last_name">
                {creditApplication.loanPurposeId === Enum.ELoanPurpose.Medical
                  ? t<string>('serviceProvider.specialistName')
                  : t<string>('serviceProvider.specialistOrDoctor')}
              </label>
              {errors.specialistName && (
                <span className="error-message">{t<string>(`loanPurpose.${errors.specialistName.message}`)}</span>
              )}
              <input type="text" id="lastName" {...register('specialistName')} maxLength={50} />
            </div>
          )}

          {merchants && merchants.length > 0 && query.length > 0 && (
            <div className="control-group center" style={{ padding: '0' }}>
              <ul className="merchant-list" style={{ cursor: 'pointer' }}>
                {merchants.slice(0, MERCHANTS_DROP_DOWN_LIMIT).map((merchant) => {
                  const queryWords = getRelevantWordsFromQuery(query)
                  const parts = merchant.name.split(' ')

                  return (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                    <li key={merchant.id} onClick={() => handleClick(merchant.id)}>
                      <div
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #BEBEBE',
                          borderRadius: '0.5rem',
                          padding: '0.5rem',
                          transition: '0.25s ease-in-out',
                          position: 'relative',
                          zIndex: 10,
                        }}
                        onMouseEnter={styleDivBackgroundF4F4F4}
                        onMouseLeave={styleDivBackgroundWhite}
                      >
                        <h3 style={{ color: '#BEBEBE', fontSize: '1.75rem' }}>
                          {parts.map((part, index) => {
                            const queryWordsContainsPart = queryWords.some((word) =>
                              removeDiacritics(part).toLowerCase().includes(removeDiacritics(word).toLowerCase()),
                            )
                            if (queryWordsContainsPart) {
                              return (
                                // eslint-disable-next-line react/no-array-index-key
                                <span key={index} style={{ color: '#505050' }}>
                                  {`${part} `}
                                </span>
                              )
                            }
                            // eslint-disable-next-line react/no-array-index-key
                            return <span key={index}>{`${part} `}</span>
                          })}
                        </h3>
                        <p style={{ fontSize: '1.25rem' }}>{merchant.address}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
              {merchants.length > MERCHANTS_DROP_DOWN_LIMIT && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  style={{
                    backgroundColor: '#08abf0',
                    padding: '1.5rem 1rem 1rem 1rem',
                    color: 'white',
                    border: '1px solid #BEBEBE',
                    borderBottomRightRadius: '0.5rem',
                    borderBottomLeftRadius: '0.5rem',
                    borderTopLeftRadius: '0',
                    borderTopRightRadius: '0',
                    marginTop: '-1rem',
                    position: 'relative',
                    zIndex: 5,
                  }}
                  onClick={handleClickContinueWithoutSelection}
                >
                  <h3 style={{ fontSize: '1.75rem' }}>
                    <Trans
                      i18nKey="serviceProvider.serviceProviderSearch.resultsFound"
                      values={{
                        merchantsFound:
                          merchants.length > MERCHANTS_DROP_DOWN_LIMIT ? MERCHANTS_DROP_DOWN_LIMIT : merchants.length,
                      }}
                    />
                  </h3>
                  <p style={{ fontSize: '1.5rem', marginTop: '1rem', cursor: 'pointer' }}>
                    <Trans i18nKey="serviceProvider.serviceProviderSearch.refineYourSearchOrContinue" />
                  </p>
                </div>
              )}
              {merchants.length > 0 && merchants.length <= MERCHANTS_DROP_DOWN_LIMIT && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  style={{
                    backgroundColor: '#08abf0',
                    padding: '1.5rem 1rem 1rem 1rem',
                    color: 'white',
                    border: '1px solid #BEBEBE',
                    borderBottomRightRadius: '0.5rem',
                    borderBottomLeftRadius: '0.5rem',
                    borderTopLeftRadius: '0',
                    borderTopRightRadius: '0',
                    marginTop: '-1rem',
                    position: 'relative',
                    zIndex: 5,
                  }}
                  onClick={handleClickContinueWithoutSelection}
                >
                  <h3 style={{ fontSize: '1.75rem' }}>
                    {t('serviceProvider.serviceProviderSearch.continueWithoutSelection')}
                  </h3>
                </div>
              )}
            </div>
          )}

          {footer(goBack, disableNext)}
          <div ref={bottomRef}> </div>
        </form>
      </section>
    </main>
  )
}

export default ServiceProviderForm
